/* eslint-disable react/no-children-prop */
import classNames from "classnames";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { Panel, Group, Separator, useGroupRef } from "react-resizable-panels";
import styles from "./styles.module.css";

type ResizeDirection = "horizontal" | "vertical";

interface Props {
  leftBlock: ReactNode;
  rightBlock: ReactNode;
  leftBlockSize?: number;
  rightBlockSize?: number;
  leftBlockMinSize?: number;
  rightBlockMinSize?: number;
  hasButton?: boolean;
  isCollapsible?: "left" | "right" | false;
  leftClassName?: string;
  rightClassName?: string;
  direction?: ResizeDirection;
}

const toPercent = (value?: number) =>
  value === undefined ? undefined : `${value}%`;

export const ResizableWrapper: FC<Props> = ({
  direction = "horizontal",
  leftBlock,
  rightBlock,
  leftBlockSize = 50,
  rightBlockSize = 50,
  leftBlockMinSize,
  rightBlockMinSize,
  leftClassName,
  rightClassName,
}) => {
  const groupRef = useGroupRef();
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isDragging) return;

    const stop = () => setIsDragging(false);
    window.addEventListener("pointerup", stop, true);
    window.addEventListener("pointercancel", stop, true);
    window.addEventListener("blur", stop);

    return () => {
      window.removeEventListener("pointerup", stop, true);
      window.removeEventListener("pointercancel", stop, true);
      window.removeEventListener("blur", stop);
    };
  }, [isDragging]);

  useEffect(() => {
    groupRef.current?.setLayout({
      left: leftBlockSize,
      right: rightBlockSize,
    });
  }, [leftBlockSize, rightBlockSize, groupRef]);

  return (
    <>
      <Group
        groupRef={groupRef}
        orientation={direction}
        className={styles.wrapper}
      >
        <Panel
          id="left"
          defaultSize={`${leftBlockSize}%`}
          minSize={rightBlockSize === 0 ? "0%" : toPercent(leftBlockMinSize)}
          className={leftClassName}
        >
          {leftBlock}
        </Panel>

        <Separator
          disabled={rightBlockSize === 0}
          onPointerDown={() => setIsDragging(true)}
          className={styles.separator}
        >
          <div
            className={classNames(
              styles["resize"],
              rightBlockSize === 0 && styles["resize-hidden"],
            )}
          >
            <span className={styles["grip"]} />
          </div>
        </Separator>

        <Panel
          id="right"
          defaultSize={`${rightBlockSize}%`}
          minSize={rightBlockSize === 0 ? "0%" : toPercent(rightBlockMinSize)}
          className={rightClassName}
        >
          {rightBlock}
        </Panel>
      </Group>

      {isDragging && (
        <div
          className={styles["drag-overlay"]}
          style={{
            cursor: direction === "horizontal" ? "col-resize" : "row-resize",
          }}
        />
      )}
    </>
  );
};
